/**
 * Indian number, currency and date formatting.
 * Every money value in the app uses these helpers so lakh/crore grouping and
 * day-first dates are applied consistently with no exceptions.
 */

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const IN_NUMBER = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const IN_NUMBER_FLEX = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** ₹ with lakh/crore grouping and exactly 2 decimals, e.g. ₹1,00,000.00 */
export function formatINR(amount: number): string {
  if (!Number.isFinite(amount)) return "₹0.00";
  return INR.format(amount);
}

/** Indian-grouped number with 2 decimals, no symbol, e.g. 1,00,000.00 */
export function formatINRNumber(amount: number): string {
  if (!Number.isFinite(amount)) return "0.00";
  return IN_NUMBER.format(amount);
}

/** Indian-grouped number with flexible decimals, e.g. 1,00,000 */
export function formatIndianNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return IN_NUMBER_FLEX.format(value);
}

/**
 * Parse an Indian-grouped input string into a number.
 * Handles "1,00,000.50", "1,00,000", whitespace, and an optional "₹" prefix.
 */
export function parseIndianNumber(input: string): number {
  const cleaned = input.replace(/[₹\s]/g, "").replace(/,/g, "");
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : NaN;
}

/** Normalize an arbitrary value to a finite number or 0. */
export function toNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

interface DateParts {
  year: number;
  month: number;
  day: number;
  weekday: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const IST_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  weekday: "short",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: false,
});

const WEEKDAY_MAP: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function getISTParts(value: string | Date | number | null | undefined): DateParts | null {
  if (value == null) return null;

  // If already a calendar date string (YYYY-MM-DD), preserve exact date components
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [y, m, d] = trimmed.split("-").map(Number);
      const dateObj = new Date(Date.UTC(y, m - 1, d));
      return {
        year: y,
        month: m,
        day: d,
        weekday: dateObj.getUTCDay(),
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
    }
  }

  const d = typeof value === "string" || typeof value === "number" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return null;

  const parts = IST_PARTS_FORMATTER.formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) {
    map[p.type] = p.value;
  }

  let h = Number(map.hour ?? 0);
  if (h === 24) h = 0;

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    weekday: WEEKDAY_MAP[map.weekday] ?? 0,
    hours: h,
    minutes: Number(map.minute ?? 0),
    seconds: Number(map.second ?? 0),
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Day-month-year in IST, e.g. 25-08-2026 (never American month-first). */
export function formatDate(value: string | Date | number | null | undefined): string {
  const ist = getISTParts(value);
  if (!ist) return "--";
  return `${pad(ist.day)}-${pad(ist.month)}-${ist.year}`;
}

/** Day-month-year + time in IST (Asia/Kolkata), e.g. 25-08-2026, 10:30 AM */
export function formatDateTime(value: string | Date | number | null | undefined): string {
  const ist = getISTParts(value);
  if (!ist) return "--";
  let hours = ist.hours;
  const minutes = pad(ist.minutes);
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${pad(ist.day)}-${pad(ist.month)}-${ist.year}, ${pad(hours)}:${minutes} ${ampm}`;
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Readable Indian-style date in IST for the top bar, e.g. "Tue, 25 Aug 2026". */
export function formatLongDate(value: string | Date | number | null | undefined): string {
  const ist = getISTParts(value);
  if (!ist) return "--";
  return `${DAYS_SHORT[ist.weekday]}, ${pad(ist.day)} ${MONTHS_SHORT[ist.month - 1]} ${ist.year}`;
}

/** Compact Indian date in IST used inside dense tables, e.g. 25-08-26. */
export function formatShortDate(value: string | Date | number | null | undefined): string {
  const ist = getISTParts(value);
  if (!ist) return "--";
  return `${pad(ist.day)}-${pad(ist.month)}-${String(ist.year).slice(2)}`;
}

/** Month name + year in IST, e.g. "August 2026". */
export function formatMonthYear(value: string | Date | number | null | undefined): string {
  const ist = getISTParts(value);
  if (!ist) return "--";
  return `${MONTHS_SHORT[ist.month - 1]} ${ist.year}`;
}